import { debouncedRef, useLocalStorage } from "@vueuse/core";
import { convert } from "geo-coordinates-parser";

const MIN_SEARCH_LENGTH = 3;
const HISTORY_ENTRIES = 3;

export function useSearch(queryRef, selected, locationRef) {
  const query = debouncedRef(queryRef, 200);
  const useMapkit = ref(true);
  const results = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const searchHistory = useSafeLocalStorage("elevation-search-history", []);

  function latLngSearch(query) {
    try {
      const converted = convert(query);
      const name = `${converted.verbatimLatitude}, ${converted.verbatimLongitude}`;
      return [
        {
          location: {
            latitude: converted.decimalLatitude,
            longitude: converted.decimalLongitude,
            name,
          },
          name,
          module: "latLng",
          type: "location",
        },
      ];
    } catch {
      return [];
    }
  }

  function currentLocationSearch(query) {
    if (query.length > 0) {
      return [];
    }
    return [
      {
        name: "Use Current Location",
        module: "currentLocation",
        type: "currentLocation",
      },
    ];
  }

  function recentSearches(query) {
    if (query.length >= MIN_SEARCH_LENGTH) {
      return [];
    }
    return searchHistory.value.map((item) => ({ ...item, module: "history" }));
  }

  async function mapkitSearch(query) {
    if (query.length < MIN_SEARCH_LENGTH || !useMapkit.value) {
      return [];
    }
    if (typeof mapkit == "undefined") {
      console.warn("mapkit not loaded");
      useMapkit.value = false;
      return [];
    }
    const coordinate =
      locationRef.value?.latitude && locationRef.value?.longitude
        ? new mapkit.Coordinate(locationRef.value.latitude, locationRef.value.longitude)
        : null;
    let search = new mapkit.Search({
      coordinate,
      includeQueries: false,
    });
    try {
      const response = await new Promise((resolve, reject) => {
        search.autocomplete(query, (error, data) => {
          if (error) {
            reject(error);
          }
          resolve(data);
        });
      });
      return response.results
        .map((item) => {
          return {
            location: {
              ...item.coordinate,
              name: item.displayLines.join(", "),
            },
            name: item.displayLines[0],
            subtitle: item.displayLines.slice(1).join(", "),
            module: "mapkitSearch",
            type: "location",
          };
        })
        .slice(0, 5);
    } catch (error) {
      console.error(error);
      useMapkit.value = false;
      return [];
    }
  }

  async function bingSearch(query) {
    if (query.length < MIN_SEARCH_LENGTH || useMapkit.value) {
      return [];
    }
    const { data, error } = await useFetchAutosuggestions(query);
    if (error.value) {
      console.error(error.value);
      return [];
    }

    return data.value.map((item) => {
      const name = item?.address?.formattedAddress;
      return {
        item,
        name,
        module: "bingSearch",
        type: "location",
      };
    });
  }

  const searchModules = [latLngSearch, mapkitSearch, bingSearch, recentSearches, currentLocationSearch];

  async function search() {
    isLoading.value = true;
    error.value = null;
    try {
      const searchResponse = await Promise.all(searchModules.map((fn) => fn(query.value)));
      results.value = searchResponse.flat();
    } catch (error) {
      error.value = error;
    } finally {
      isLoading.value = false;
    }
  }

  watch(selected, () => {
    if (selected.value?.module.includes("currentLocation", "history") || !selected.value?.location) {
      return;
    }

    // add selected to start of history
    searchHistory.value.unshift(selected.value);

    // remove duplicates and limit number of entries
    searchHistory.value = searchHistory.value
      .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name))
      .slice(0, HISTORY_ENTRIES);
  });

  watch(selected, async () => {
    if (selected.value?.module !== "bingSearch" || selected.value?.location) {
      return;
    }
    const { data: location, error } = await getLocationForAutosuggestion(selected.value);
    if (location.value) {
      selected.value = {
        ...selected.value,
        addressItem: location.value,
        location: {
          latitude: location.value?.point?.coordinates[0],
          longitude: location.value?.point?.coordinates[1],
          name: location.value.address?.formattedAddress,
        },
      };
    }
  });

  watch(query, search);
  search();

  return {
    results,
    isLoading,
    error,
  };
}
